import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getAppeals,
  getComplaints,
  getDepartments,
  getBranches,
  getEmployees,
  getLeaveRequests,
  getPenalties,
  getRequests,
  getAdvances,
  getOvertimes,
  getWeeklyRestRequests,
  getPermitRequests,
  listTimesheets,
  getInventoryNights,
  getWfhRequests,
  listResignations,
  getRecruitmentInboxCounts,
  getMyPendingEvaluationCount,
} from "../api";
import { mapApiResignationForReview, isPendingResignationStatus } from "../utils/resignHelpers";
import {
  ADMINISTRATION_KEY,
  mapAdvanceRow,
  mapOvertimeRow,
  mapBranches,
  mapComplaintRow,
  mapDepartments,
  enrichEmployeesWithDepartmentManagers,
  groupEmployeesByBranch,
  mapEmployeeRow,
  mapLeaveToPendingRequest,
  mapWeeklyRestToPendingRequest,
  mapTimesheetToPendingRequest,
  mapPermitToPendingRequest,
  mapPenaltyRow,
  mapRequestToPendingRequest,
  mapInventoryNightToPendingRequest,
  formatWorkplaceLabel,
} from "../utils/dataMappers";
import { enrichPenaltiesWithAppealStatus } from "../utils/penaltyAppealStatus";
import {
  findEmployeeByRole,
  getAllEmployeesFromData,
} from "../utils/roleHelpers";
import { isHrFamilyRole } from "../utils/hrStaffHelpers";
import {
  collectWeeklyRestCalendarDays,
  countCurrentlyOnLeaveEmployees,
} from "../utils/currentLeaveHelpers";
import {
  getCurrentMonthKey,
  getPenaltyMonthKey,
} from "../utils/penaltyMonthHelpers";
import {
  buildManagerTeamMembers,
  collectSubordinateEmployees,
  uniqueEmployeesById,
} from "../utils/reportingTree";

function withEmployeeWorkplace(mapped, emp) {
  if (!emp) return mapped;
  const branchName = mapped.branchName || emp.branchName || "";
  const departmentName = mapped.departmentName || emp.departmentName || "";
  return {
    ...mapped,
    employeeId: emp.id || mapped.employeeId,
    employeeName: emp.name || mapped.employeeName,
    employeeCode: emp.code || mapped.employeeCode,
    jobTitle: mapped.jobTitle || emp.jobTitle || null,
    managerId: mapped.managerId || emp.managerId || null,
    requesterRoleSlugs: emp.roleSlugs || mapped.requesterRoleSlugs || [],
    branchName,
    departmentName,
    workplaceLabel:
      mapped.workplaceLabel ||
      formatWorkplaceLabel(branchName, departmentName),
  };
}

function unwrapListPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function indexEmployees(employeeRows) {
  const byUserId = new Map();
  const byId = new Map();
  const byCode = new Map();
  for (const employee of employeeRows) {
    if (employee.userId) byUserId.set(employee.userId, employee);
    if (employee.id) byId.set(employee.id, employee);
    if (employee.code) byCode.set(employee.code, employee);
  }
  return { byUserId, byId, byCode };
}

function findIndexedEmployee(index, mapped) {
  return (
    (mapped?.userId && index.byUserId.get(mapped.userId)) ||
    (mapped?.employeeId && index.byId.get(mapped.employeeId)) ||
    (mapped?.senderId && index.byUserId.get(mapped.senderId)) ||
    (mapped?.employeeCode && index.byCode.get(mapped.employeeCode)) ||
    null
  );
}

function mapAppealForClient(appeal) {
  if (!appeal) return appeal;
  return {
    ...appeal,
    jobTitle: appeal.jobTitle || null,
    workplaceLabel:
      appeal.workplaceLabel ||
      formatWorkplaceLabel(appeal.branchName, appeal.departmentName),
  };
}

function penaltyStatusAfterAppeal(appeal) {
  if (appeal?.finalStatus === "approved") return "cancelled";
  if (appeal?.finalStatus === "rejected") return "active";
  return null;
}

const APPEAL_DECISION_TTL_MS = 60_000;

function pruneAppealDecisions(store) {
  const now = Date.now();
  const next = new Map();
  store.forEach((entry, id) => {
    if (now - entry.at <= APPEAL_DECISION_TTL_MS) next.set(id, entry);
  });
  return next;
}

function overlayAppealDecisions(appeals, storeRef) {
  storeRef.current = pruneAppealDecisions(storeRef.current);
  if (!storeRef.current.size) return appeals;
  return appeals.map((row) => {
    const entry = storeRef.current.get(row.id);
    if (!entry) return row;
    const sameDecision =
      (row.finalStatus || "") === (entry.appeal.finalStatus || "") &&
      (row.currentLevel || "") === (entry.appeal.currentLevel || "") &&
      (row.status || "") === (entry.appeal.status || "");
    if (sameDecision) {
      storeRef.current.delete(row.id);
      return row;
    }
    return { ...row, ...entry.appeal };
  });
}

function applyRememberedPenaltyStatuses(penalties, appeals) {
  const byPenalty = new Map();
  appeals.forEach((appeal) => {
    const status = penaltyStatusAfterAppeal(appeal);
    if (appeal?.penaltyId && status) {
      byPenalty.set(String(appeal.penaltyId), status);
    }
  });
  if (!byPenalty.size) return penalties;
  return penalties.map((penalty) => {
    const nextStatus = byPenalty.get(String(penalty.id));
    if (!nextStatus || penalty.status === nextStatus) return penalty;
    return {
      ...penalty,
      status: nextStatus,
      appealStatus:
        nextStatus === "cancelled"
          ? "مقبول"
          : nextStatus === "active"
            ? null
            : penalty.appealStatus,
    };
  });
}

function dataFingerprint(next) {
  const pack = (list, pick) => (list || []).map(pick).join(",");
  const employees = Object.values(next.employeesByBranch || {}).flat();
  return [
    pack(next.branches, (row) => row.id),
    pack(next.departments, (row) => `${row.id}:${row.name}`),
    pack(employees, (row) => `${row.id}:${row.status}:${row.name}`),
    pack(next.pendingRequests, (row) => `${row.id}:${row.status}`),
    pack(next.complaints, (row) => `${row.id}:${row.status}`),
    pack(next.resignations, (row) => `${row.id}:${row.status}`),
    pack(next.allPenalties, (row) => `${row.id}:${row.status}`),
    pack(next.appeals, (row) => `${row.id}:${row.finalStatus || ""}:${row.currentLevel || ""}`),
    pack(next.wfhRequests, (row) => `${row.id}:${row.status}`),
    pack(
      next.timesheets,
      (row) => `${row.id}:${row.status}:${(row.employees || []).length}`,
    ),
    `${next.recruitmentInbox?.requisitions}|${next.recruitmentInbox?.offers}|${next.recruitmentInbox?.interviews}`,
    String(Number(next.pendingEvaluationCount) || 0),
  ].join("|");
}

const DataContext = createContext(null);

/** How often the open app reloads inbox data (approvals, badges, requests). */
const DATA_POLL_MS = 45_000;
/** Employee directory changes rarely; skip it on the fast poll. */
const ORG_REFRESH_MS = 3 * 60 * 1000;
/** Home badges only need recent weeks plus anything still pending. */
const TIMESHEET_RECENT_WEEKS = 12;

const EMPTY_DATA = {
  branches: [],
  departments: [],
  employeesByBranch: { [ADMINISTRATION_KEY]: [] },
  teamMembers: [],
  pendingRequests: [],
  leaveCalendarData: [],
  timesheetRestCalendarData: [],
  timesheets: [],
  employeePenalties: [],
  managerPenalties: [],
  handovers: [],
  recentActivities: [],
  allPenalties: [],
  appeals: [],
  complaints: [],
  resignations: [],
  wfhRequests: [],
  recruitmentInbox: { requisitions: 0, offers: 0, interviews: 0 },
  pendingEvaluationCount: 0,
};

function dataSessionKey(user, isAuthenticated) {
  if (isAuthenticated && user?._fromBackend && user?.id != null) {
    return String(user.id);
  }
  return "guest";
}

export function DataProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(() => Boolean(user?._fromBackend));
  const [error, setError] = useState(null);
  const [readyForSession, setReadyForSession] = useState(null);

  const sessionKey = dataSessionKey(user, isAuthenticated);
  const hasLoaded = readyForSession === sessionKey;
  const refreshGen = useRef(0);
  /** Non-zero while a visible refresh owns the loading flag. Silent polls must not cancel it. */
  const foregroundGen = useRef(0);
  const orgCacheRef = useRef({ at: 0, departmentsRaw: null, branchesRaw: null, employeesRaw: null });
  const snapshotRef = useRef("");
  const recentAppealDecisionsRef = useRef(new Map());

  const refreshData = useCallback(async (options) => {
    const silent = options?.silent === true || options?.scope === "appeals";

    const isPlatformAdmin =
      user?.role === "platform-admin" ||
      user?.backendRoles?.includes("platform_admin");
    if (isPlatformAdmin) {
      if (silent) return;
      snapshotRef.current = "";
      setData(EMPTY_DATA);
      setError(null);
      setLoading(false);
      setReadyForSession(dataSessionKey(user, true));
      return;
    }

    if (!isAuthenticated || !user?._fromBackend) {
      if (silent) return;
      orgCacheRef.current = {
        at: 0,
        departmentsRaw: null,
        branchesRaw: null,
        employeesRaw: null,
      };
      snapshotRef.current = "";
      setData(EMPTY_DATA);
      setLoading(false);
      setReadyForSession("guest");
      return;
    }

    if (silent && foregroundGen.current !== 0) return;

    const gen = ++refreshGen.current;
    if (!silent) foregroundGen.current = gen;

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    const appealsOnly = options?.scope === "appeals";
    const inboxOnly = options?.inboxOnly === true;
    const refreshOrg = !inboxOnly && !appealsOnly && (options?.refreshOrg === true || !silent);
    const orgCache = orgCacheRef.current;
    const loadOrg =
      !orgCache.employeesRaw ||
      (!inboxOnly &&
        (refreshOrg || Date.now() - orgCache.at >= ORG_REFRESH_MS));

    try {
      if (appealsOnly) {
        const results = await Promise.allSettled([getAppeals(), getPenalties()]);
        if (silent && results.some((result) => result.status !== "fulfilled")) {
          return;
        }
        const mappedAppeals = overlayAppealDecisions(
          unwrapListPayload(
            results[0].status === "fulfilled" ? results[0].value : [],
          ).map(mapAppealForClient),
          recentAppealDecisionsRef,
        );
        const penaltiesRaw =
          results[1].status === "fulfilled" ? results[1].value : [];
        if (gen !== refreshGen.current) return;
        setData((prev) => {
          const mappedPenalties = enrichPenaltiesWithAppealStatus(
            applyRememberedPenaltyStatuses(
              unwrapListPayload(penaltiesRaw).map(mapPenaltyRow),
              mappedAppeals,
            ),
            mappedAppeals,
            prev.employeesByBranch,
          );
          const myEmployeeId = user?.employeeId;
          const ownPenalties = myEmployeeId
            ? mappedPenalties.filter(
                (p) =>
                  String(p.employeeId) === String(myEmployeeId) &&
                  p.status !== "cancelled",
              )
            : [];
          const merged = {
            ...prev,
            appeals: mappedAppeals,
            allPenalties: mappedPenalties,
            employeePenalties: mappedPenalties,
            managerPenalties: ownPenalties,
          };
          const snap = dataFingerprint(merged);
          if (snap === snapshotRef.current) return prev;
          snapshotRef.current = snap;
          return merged;
        });
        return;
      }

      const results = await Promise.allSettled([
        // Include inactive so org structure can surface departments that were
        // previously soft-deactivated (e.g. older seed) but still have staff.
        loadOrg
          ? getDepartments({ includeInactive: true })
          : Promise.resolve(orgCache.departmentsRaw),
        loadOrg ? getBranches() : Promise.resolve(orgCache.branchesRaw),
        loadOrg ? getEmployees() : Promise.resolve(orgCache.employeesRaw),
        getLeaveRequests({ limit: 100 }),
        getRequests(),
        inboxOnly ? Promise.resolve([]) : getComplaints(),
        inboxOnly ? Promise.resolve([]) : getPenalties(),
        inboxOnly ? Promise.resolve([]) : getAppeals(),
        getAdvances(),
        getOvertimes(),
        getWeeklyRestRequests(),
        getPermitRequests(),
        listTimesheets({ recentWeeks: TIMESHEET_RECENT_WEEKS }),
        getInventoryNights(),
        getWfhRequests({ status: "PENDING" }),
        inboxOnly ? Promise.resolve([]) : listResignations(),
        inboxOnly ? Promise.resolve({}) : getRecruitmentInboxCounts(),
        getMyPendingEvaluationCount().catch(() => ({ count: 0 })),
      ]);

      // A background refresh must not wipe the inbox when one call fails.
      if (silent && results.some((result) => result.status !== "fulfilled")) {
        return;
      }

      const departmentsRaw =
        results[0].status === "fulfilled" ? results[0].value : [];
      const branchesRaw =
        results[1].status === "fulfilled" ? results[1].value : [];
      const employeesRaw =
        results[2].status === "fulfilled" ? results[2].value : [];
      const leavesRaw =
        results[3].status === "fulfilled" ? results[3].value : [];
      const requestsRaw =
        results[4].status === "fulfilled" ? results[4].value : [];
      const complaintsRaw =
        results[5].status === "fulfilled" ? results[5].value : [];
      const penaltiesRaw =
        results[6].status === "fulfilled" ? results[6].value : [];
      const appealsRaw =
        results[7].status === "fulfilled" ? results[7].value : [];
      const advancesRaw =
        results[8].status === "fulfilled" ? results[8].value : [];
      const overtimesRaw =
        results[9].status === "fulfilled" ? results[9].value : [];
      const weeklyRestsRaw =
        results[10].status === "fulfilled" ? results[10].value : [];
      const permitsRaw =
        results[11].status === "fulfilled" ? results[11].value : [];
      const timesheetsRaw =
        results[12].status === "fulfilled" ? results[12].value : [];
      const inventoryNightsRaw =
        results[13].status === "fulfilled" ? results[13].value : [];
      const wfhRaw =
        results[14].status === "fulfilled" ? results[14].value : [];
      const resignationsRaw =
        results[15].status === "fulfilled" ? results[15].value : [];
      const recruitmentInboxRaw =
        results[16].status === "fulfilled" ? results[16].value : {};
      const pendingEvaluationCountRaw =
        results[17].status === "fulfilled" ? results[17].value : { count: 0 };

      const departments = mapDepartments(unwrapListPayload(departmentsRaw));
      const branches = mapBranches(unwrapListPayload(branchesRaw));
      const employees = unwrapListPayload(employeesRaw);
      if (loadOrg && results[0].status === "fulfilled" && results[1].status === "fulfilled" && results[2].status === "fulfilled") {
        orgCacheRef.current = {
          at: Date.now(),
          departmentsRaw,
          branchesRaw,
          employeesRaw,
        };
      }

      const employeeRows = enrichEmployeesWithDepartmentManagers(
        employees.map(mapEmployeeRow),
      );
      const employeesByBranch = groupEmployeesByBranch(employeeRows);
      const employeeIndex = indexEmployees(employeeRows);

      const leaveRequests = unwrapListPayload(leavesRaw).map((leave) => {
        const mapped = mapLeaveToPendingRequest(leave);
        const emp = findIndexedEmployee(employeeIndex, mapped);

        if (!emp) return mapped;

        const submittedByRole =
          mapped.submittedByRole ||
          (mapped.workflow?.requestType === "leave_direct_gm"
            ? emp.memberLevel === "department-manager"
              ? "department-manager"
              : emp.memberLevel === "hr"
                ? "hr"
                : undefined
            : undefined);

        return {
          ...withEmployeeWorkplace(mapped, emp),
          ...(submittedByRole ? { submittedByRole } : {}),
        };
      });

      // Keep one leave row per requestId/id (guards against duplicate API rows)
      const uniqueLeaveRequests = [];
      const seenLeaveKeys = new Set();
      for (const leave of leaveRequests) {
        const key = leave.requestId || leave.id;
        if (seenLeaveKeys.has(key)) continue;
        seenLeaveKeys.add(key);
        uniqueLeaveRequests.push(leave);
      }

      const genericRequests = unwrapListPayload(requestsRaw)
        .filter(
          (r) =>
            r.type !== "leave" &&
            r.type !== "complaint" &&
            r.type !== "resignation" &&
            r.type !== "advance" &&
            r.type !== "overtime" &&
            r.type !== "weekly_rest" &&
            r.type !== "permit" &&
            r.type !== "timesheet" &&
            r.type !== "inventory_night",
        )
        .map(mapRequestToPendingRequest);

      const advanceRequests = unwrapListPayload(advancesRaw).map((advance) => {
        const mapped = mapAdvanceRow(advance);
        const emp = findIndexedEmployee(employeeIndex, mapped);

        if (!emp) return mapped;

        const submittedByRole =
          mapped.submittedByRole ||
          (mapped.workflow?.requestType === "advance_direct_gm"
            ? emp.memberLevel === "department-manager"
              ? "department-manager"
              : emp.memberLevel === "hr"
                ? "hr"
                : undefined
            : undefined);

        return {
          ...withEmployeeWorkplace(mapped, emp),
          ...(submittedByRole ? { submittedByRole } : {}),
        };
      });

      const overtimeRequests = unwrapListPayload(overtimesRaw).map((row) => {
        const mapped = mapOvertimeRow(row);
        const emp = findIndexedEmployee(employeeIndex, mapped);

        if (!emp) return mapped;
        return withEmployeeWorkplace(mapped, emp);
      });

      const weeklyRestRequests = unwrapListPayload(weeklyRestsRaw).map(
        (row) => {
          const mapped = mapWeeklyRestToPendingRequest(row);
          const emp = findIndexedEmployee(employeeIndex, mapped);

          if (!emp) return mapped;
          return withEmployeeWorkplace(mapped, emp);
        },
      );

      const permitRequests = unwrapListPayload(permitsRaw).map((row) => {
        const mapped = mapPermitToPendingRequest(row);
        const emp = findIndexedEmployee(employeeIndex, mapped);

        if (!emp) return mapped;

        const submittedByRole =
          mapped.submittedByRole ||
          (mapped.workflow?.requestType === "permit_direct_gm"
            ? emp.memberLevel === "department-manager"
              ? "department-manager"
              : emp.memberLevel === "hr"
                ? "hr"
                : undefined
            : undefined);

        return {
          ...withEmployeeWorkplace(mapped, emp),
          ...(submittedByRole ? { submittedByRole } : {}),
        };
      });

      const timesheetRequests = unwrapListPayload(timesheetsRaw)
        .filter((row) => row.status === "PENDING" || row.status === "pending")
        .filter(
          (row) => row.scope !== "BRANCH" && row.scope !== "CASHIER",
        )
        .map(mapTimesheetToPendingRequest);

      const inventoryNightRequests = unwrapListPayload(inventoryNightsRaw)
        .filter(
          (row) =>
            row.status === "PENDING" ||
            row.requestStatus === "PENDING" ||
            row.requestStatus === "IN_PROGRESS",
        )
        .map(mapInventoryNightToPendingRequest);

      const pendingRequests = [
        ...uniqueLeaveRequests,
        ...advanceRequests,
        ...overtimeRequests,
        ...weeklyRestRequests,
        ...permitRequests,
        ...timesheetRequests,
        ...inventoryNightRequests,
        ...genericRequests,
      ];

      const leaveCalendarData = uniqueLeaveRequests
        .filter((r) => r.startDate && r.status === "approved")
        .map((r) => ({
          date: r.startDate,
          employee: r.employeeName,
          type: r.type,
          days: r.days,
        }));

      const timesheets = unwrapListPayload(timesheetsRaw);
      const timesheetRestCalendarData =
        collectWeeklyRestCalendarDays(timesheets);

      const isBranchManager = user.backendRoles?.includes("branch_manager");
      const isRegionalManager = user.backendRoles?.includes("regional_manager");
      const isDeptManager =
        user.backendRoles?.includes("department_manager") ||
        user.role === "department-manager";
      const isGm = user.role === "gm" || user.role === "owner";
      const managedBranchIds = user.managedBranchIds || [];

      const teamMembers = buildManagerTeamMembers({
        employeeRows,
        user: { ...user, managedBranchIds },
        isGm,
        isBranchManager,
        isRegionalManager,
        isDeptManager,
      });

      const mappedAppeals = overlayAppealDecisions(
        unwrapListPayload(appealsRaw).map(mapAppealForClient),
        recentAppealDecisionsRef,
      );
      const mappedPenalties = enrichPenaltiesWithAppealStatus(
        applyRememberedPenaltyStatuses(
          unwrapListPayload(penaltiesRaw).map(mapPenaltyRow),
          mappedAppeals,
        ),
        mappedAppeals,
        employeesByBranch,
      );
      const myEmployeeId = user?.employeeId;
      const ownPenalties = myEmployeeId
        ? mappedPenalties.filter(
            (p) =>
              String(p.employeeId) === String(myEmployeeId) &&
              p.status !== "cancelled",
          )
        : [];

      if (gen !== refreshGen.current) return;

      const nextData = {
        branches,
        departments,
        employeesByBranch,
        teamMembers,
        pendingRequests,
        leaveCalendarData,
        timesheetRestCalendarData,
        timesheets,
        employeePenalties: mappedPenalties,
        // Own penalties for manager/DM personal home (appeal UI)
        managerPenalties: ownPenalties,
        handovers: [],
        recentActivities: [],
        allPenalties: mappedPenalties,
        appeals: mappedAppeals,
        complaints: unwrapListPayload(complaintsRaw).map((row) => {
          const mapped = mapComplaintRow(row);
          if (mapped.workplaceLabel && mapped.jobTitle) return mapped;
          const emp =
            findIndexedEmployee(employeeIndex, mapped) ||
            employeeRows.find((e) => e.name && e.name === mapped.senderName);
          if (!emp) return mapped;
          return {
            ...mapped,
            jobTitle: mapped.jobTitle || emp.jobTitle || null,
            branchName: mapped.branchName || emp.branchName || "",
            departmentName: mapped.departmentName || emp.departmentName || "",
            workplaceLabel:
              mapped.workplaceLabel ||
              formatWorkplaceLabel(emp.branchName, emp.departmentName),
          };
        }),
        resignations: unwrapListPayload(resignationsRaw)
          .map((row) => {
            const mapped = mapApiResignationForReview(row);
            if (!mapped) return mapped;
            if (mapped.workplaceLabel) return mapped;
            const emp = findIndexedEmployee(employeeIndex, {
              userId: mapped.employeeId,
              employeeId: mapped.employeeRecordId,
            });
            if (!emp) return mapped;
            return {
              ...mapped,
              branchName: emp.branchName || "",
              departmentName: emp.departmentName || "",
              workplaceLabel: formatWorkplaceLabel(
                emp.branchName,
                emp.departmentName,
              ),
            };
          })
          .filter(Boolean),
        wfhRequests: unwrapListPayload(wfhRaw),
        recruitmentInbox: {
          requisitions: Number(recruitmentInboxRaw?.requisitions) || 0,
          offers: Number(recruitmentInboxRaw?.offers) || 0,
          interviews: Number(recruitmentInboxRaw?.interviews) || 0,
        },
        pendingEvaluationCount:
          Number(pendingEvaluationCountRaw?.count) || 0,
      };
      setData((prev) => {
        const merged = inboxOnly
          ? {
              ...nextData,
              employeePenalties: prev.employeePenalties,
              managerPenalties: prev.managerPenalties,
              allPenalties: prev.allPenalties,
              appeals: prev.appeals,
              complaints: prev.complaints,
              resignations: prev.resignations,
              recruitmentInbox: prev.recruitmentInbox,
              handovers: prev.handovers,
              recentActivities: prev.recentActivities,
            }
          : {
              ...nextData,
              handovers: prev.handovers,
              recentActivities: prev.recentActivities,
            };
        const snap = dataFingerprint(merged);
        if (snap === snapshotRef.current) return prev;
        snapshotRef.current = snap;
        return merged;
      });
    } catch (err) {
      if (gen !== refreshGen.current) return;
      console.error("Failed to load application data", err);
      if (!silent) {
        setError("تعذر تحميل البيانات من الخادم");
        setData(EMPTY_DATA);
      }
    } finally {
      if (foregroundGen.current === gen) foregroundGen.current = 0;
      if (gen !== refreshGen.current) return;
      if (!silent) setLoading(false);
      setReadyForSession(dataSessionKey(user, true));
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Keep the approvals inbox current while the app stays open.
  useEffect(() => {
    if (!isAuthenticated || !user?._fromBackend) return undefined;

    let timer = null;

    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      refreshData({ silent: true, inboxOnly: true });
    };

    const start = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(tick, DATA_POLL_MS);
    };

    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      refreshData({ silent: true, refreshOrg: true });
    };

    start();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("app:resume", onVisible);
    window.addEventListener("push:received", tick);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("app:resume", onVisible);
      window.removeEventListener("push:received", tick);
    };
  }, [isAuthenticated, user?._fromBackend, refreshData]);

  const updatePendingRequests = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      pendingRequests:
        typeof updater === "function" ? updater(prev.pendingRequests) : updater,
    }));
  }, []);

  const updateWfhRequests = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      wfhRequests:
        typeof updater === "function" ? updater(prev.wfhRequests) : updater,
    }));
  }, []);

  const addPendingRequest = useCallback(
    async () => {
      await refreshData();
    },
    [refreshData],
  );

  const updateTeamMembers = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      teamMembers:
        typeof updater === "function" ? updater(prev.teamMembers) : updater,
    }));
  }, []);

  const updateEmployeesByBranch = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      employeesByBranch:
        typeof updater === "function"
          ? updater(prev.employeesByBranch)
          : updater,
    }));
  }, []);

  const updateHandovers = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      handovers:
        typeof updater === "function" ? updater(prev.handovers) : updater,
    }));
  }, []);

  const updatePenalties = useCallback((role, updater) => {
    setData((prev) => ({
      ...prev,
      [role === "department-manager"
        ? "managerPenalties"
        : "employeePenalties"]:
        typeof updater === "function"
          ? updater(
              prev[
                role === "department-manager"
                  ? "managerPenalties"
                  : "employeePenalties"
              ],
            )
          : updater,
    }));
  }, []);

  const addPenalty = useCallback((penalty) => {
    setData((prev) => ({
      ...prev,
      allPenalties: [penalty, ...prev.allPenalties],
    }));
  }, []);

  const addAppeal = useCallback((appeal) => {
    setData((prev) => ({
      ...prev,
      appeals: [appeal, ...prev.appeals],
    }));
  }, []);

  const updateAppeals = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      appeals: typeof updater === "function" ? updater(prev.appeals) : updater,
    }));
  }, []);

  const applyAppealDecision = useCallback(
    (appeal) => {
      const mapped = mapAppealForClient(appeal);
      if (!mapped?.id) return;
      recentAppealDecisionsRef.current.set(mapped.id, {
        appeal: mapped,
        at: Date.now(),
      });

      setData((prev) => {
        const appeals = prev.appeals.some((row) => row.id === mapped.id)
          ? prev.appeals.map((row) =>
              row.id === mapped.id ? { ...row, ...mapped } : row,
            )
          : [mapped, ...prev.appeals];
        const nextPenaltyStatus = penaltyStatusAfterAppeal(mapped);
        const nextPenalties =
          mapped.penaltyId && nextPenaltyStatus
            ? prev.allPenalties.map((penalty) =>
                String(penalty.id) === String(mapped.penaltyId)
                  ? {
                      ...penalty,
                      status: nextPenaltyStatus,
                      appealStatus:
                        nextPenaltyStatus === "cancelled"
                          ? "مقبول"
                          : nextPenaltyStatus === "active"
                            ? null
                            : penalty.appealStatus,
                    }
                  : penalty,
              )
            : enrichPenaltiesWithAppealStatus(
                prev.allPenalties,
                appeals,
                prev.employeesByBranch,
              );
        const myEmployeeId = user?.employeeId;
        const ownPenalties = myEmployeeId
          ? nextPenalties.filter(
              (penalty) =>
                String(penalty.employeeId) === String(myEmployeeId) &&
                penalty.status !== "cancelled",
            )
          : prev.managerPenalties;
        const merged = {
          ...prev,
          appeals,
          allPenalties: nextPenalties,
          employeePenalties: nextPenalties,
          managerPenalties: ownPenalties,
        };
        snapshotRef.current = dataFingerprint(merged);
        return merged;
      });
    },
    [user?.employeeId],
  );

  const addRecentActivity = useCallback((activity) => {
    setData((prev) => ({
      ...prev,
      recentActivities: [activity, ...prev.recentActivities].slice(0, 20),
    }));
  }, []);

  const addComplaint = useCallback(
    async () => {
      await refreshData();
    },
    [refreshData],
  );

  const updateComplaints = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      complaints:
        typeof updater === "function" ? updater(prev.complaints) : updater,
    }));
  }, []);

  const addResignation = useCallback((resignation) => {
    setData((prev) => ({
      ...prev,
      resignations: [resignation, ...prev.resignations],
    }));
  }, []);

  const updateResignations = useCallback((updater) => {
    setData((prev) => ({
      ...prev,
      resignations:
        typeof updater === "function" ? updater(prev.resignations) : updater,
    }));
  }, []);

  const getEmployeeById = useCallback(
    (employeeId) => {
      const allEmployees = Object.values(data.employeesByBranch).flat();
      return (
        allEmployees.find((e) => e.id === employeeId) ||
        data.teamMembers.find((e) => e.id === employeeId)
      );
    },
    [data],
  );

  const getManagedEmployees = useCallback(
    (managerId, role) => {
      const allEmployees = Object.values(data.employeesByBranch).flat();
      if (isHrFamilyRole(role) || role === "gm" || role === "owner" || role === "admin")
        return allEmployees;
      if (!managerId) return [];
      const subtree = collectSubordinateEmployees(allEmployees, managerId, {
        includeTerminated: true,
      });
      if (role === "department-manager") {
        const manager = allEmployees.find(
          (e) => String(e.id) === String(managerId),
        );
        const byDepartment = allEmployees.filter(
          (e) =>
            String(e.departmentManagerId) === String(managerId) ||
            (manager?.departmentBackendId &&
              e.departmentBackendId === manager.departmentBackendId) ||
            (manager?.departmentId && e.departmentId === manager.departmentId),
        );
        return uniqueEmployeesById(subtree.concat(byDepartment));
      }
      if (role === "manager") {
        return subtree;
      }
      return [];
    },
    [data],
  );

  const getAllEmployees = useCallback(
    () => getAllEmployeesFromData(data),
    [data],
  );

  const findRoleEmployee = useCallback(
    (role) => findEmployeeByRole(getAllEmployeesFromData(data), role),
    [data],
  );

  const getHrStats = useCallback(() => {
    const allEmployees = Object.values(data.employeesByBranch).flat();
    const activeEmployees = allEmployees.filter((e) => e.status === "active");
    const allPenalties = data.allPenalties.length
      ? data.allPenalties
      : data.employeePenalties;
    const currentMonthKey = getCurrentMonthKey();
    const currentMonthPenalties = allPenalties.filter(
      (p) => getPenaltyMonthKey(p) === currentMonthKey,
    );
    const isLeaveType = (type) =>
      type?.includes("إجازة") || type === "إذن" || type?.includes("مرضية");
    const leaveRequests = data.pendingRequests.filter((r) =>
      isLeaveType(r.type),
    );
    const openComplaintStatuses = ["Pending", "In Progress"];

    return {
      // Dashboard "إجمالي الموظفين" = active employees only
      totalEmployees: activeEmployees.length,
      activeEmployees: activeEmployees.length,
      onLeaveEmployees: countCurrentlyOnLeaveEmployees(
        allEmployees,
        data.pendingRequests,
        data.timesheets,
      ),
      totalBranches: data.branches.length,
      pendingRequests: data.pendingRequests.filter((r) => r.status === "pending")
        .length,
      pendingLeaveRequests: leaveRequests.filter((r) => r.status === "pending")
        .length,
      // Dashboard penalties card = current calendar month only
      activePenalties: currentMonthPenalties.filter((p) => p.status === "active")
        .length,
      pendingHrPenalties: allPenalties.filter((p) => p.status === "pending_hr")
        .length,
      appealedPenalties: allPenalties.filter((p) => p.status === "appealed")
        .length,
      totalPenalties: currentMonthPenalties.length,
      openComplaints: data.complaints.filter((c) =>
        openComplaintStatuses.includes(c.status),
      ).length,
      totalComplaints: data.complaints.length,
      hrPendingAppeals: data.appeals.filter(
        (a) =>
          a.currentLevel === "hr" &&
          a.levels?.hr?.status === "pending" &&
          !a.finalStatus,
      ).length,
      allPendingAppeals: data.appeals.filter((a) => !a.finalStatus).length,
    };
  }, [data]);

  const getStats = useCallback(() => {
    const totalEmployees = Object.values(data.employeesByBranch).flat().length;
    const activeEmployees = Object.values(data.employeesByBranch)
      .flat()
      .filter((e) => e.status === "active").length;
    const onLeaveEmployees = countCurrentlyOnLeaveEmployees(
      Object.values(data.employeesByBranch).flat(),
      data.pendingRequests,
      data.timesheets,
    );
    const pendingApprovals = data.pendingRequests.filter(
      (r) => r.status === "pending",
    ).length;

    return {
      totalEmployees,
      activeEmployees,
      onLeaveEmployees,
      pendingApprovals,
      totalBranches: data.branches.length,
      teamSize: data.teamMembers.length,
      activeTeam: data.teamMembers.filter((m) => m.status === "active").length,
      onLeaveTeam: countCurrentlyOnLeaveEmployees(
        data.teamMembers,
        data.pendingRequests,
        data.timesheets,
      ),
    };
  }, [data]);

  const getGmStats = useCallback(() => {
    const hrStats = getHrStats();
    const advanceRequests = data.pendingRequests.filter((r) => r.type === "سلفة");
    const pendingResignations = data.resignations.filter((r) =>
      isPendingResignationStatus(r.status),
    ).length;
    const activeHandovers = data.handovers.filter(
      (h) => h.status === "active",
    ).length;
    const departmentManagers = Object.values(data.employeesByBranch)
      .flat()
      .filter((e) => e.memberLevel === "department-manager").length;
    const branchManagers = Object.values(data.employeesByBranch)
      .flat()
      .filter(
        (e) =>
          e.memberLevel === "branch-manager" || e.jobTitle?.includes("مدير فرع"),
      ).length;

    return {
      ...hrStats,
      totalDepartments: data.departments.length,
      departmentManagers,
      branchManagers,
      pendingAdvances: advanceRequests.filter((r) => r.status === "pending")
        .length,
      totalAdvances: advanceRequests.length,
      pendingResignations,
      totalResignations: data.resignations.length,
      activeHandovers,
      totalHandovers: data.handovers.length,
    };
  }, [data, getHrStats]);

  const value = useMemo(
    () => ({
      ...data,
      loading,
      hasLoaded,
      error,
      refreshData,
      updatePendingRequests,
      updateWfhRequests,
      addPendingRequest,
      updateTeamMembers,
      updateEmployeesByBranch,
      updateHandovers,
      updatePenalties,
      addPenalty,
      addAppeal,
      updateAppeals,
      applyAppealDecision,
      addRecentActivity,
      addComplaint,
      updateComplaints,
      addResignation,
      updateResignations,
      getEmployeeById,
      getManagedEmployees,
      getAllEmployees,
      findRoleEmployee,
      getStats,
      getHrStats,
      getGmStats,
    }),
    [
      data,
      loading,
      hasLoaded,
      error,
      refreshData,
      updatePendingRequests,
      updateWfhRequests,
      addPendingRequest,
      updateTeamMembers,
      updateEmployeesByBranch,
      updateHandovers,
      updatePenalties,
      addPenalty,
      addAppeal,
      updateAppeals,
      applyAppealDecision,
      addRecentActivity,
      addComplaint,
      updateComplaints,
      addResignation,
      updateResignations,
      getEmployeeById,
      getManagedEmployees,
      getAllEmployees,
      findRoleEmployee,
      getStats,
      getHrStats,
      getGmStats,
    ],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}
